import { useEffect, useRef, useState } from 'react';

export default function About() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="w-full bg-[#f2ede8] py-16 px-10 md:px-20">
      <div ref={ref} className="max-w-screen-xl mx-auto">

        {/* Section Label */}
        <span className="font-sans-kr text-[11px] tracking-[0.25em] uppercase text-[#888] mb-8 block">
          About
        </span>

        <div
          className={`flex flex-col lg:flex-row gap-12 xl:gap-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {/* ── Left Column ── */}
          <div className="lg:w-[38%] flex flex-col justify-between">
            {/* Big Display Heading */}
            <h2 className="font-korean-serif text-[clamp(2rem,3.8vw,3rem)] leading-[1.25] font-bold text-[#1a1a1a] mb-6">
              발상의<br />
              전환을<br />
              만드는 곳.
            </h2>

            {/* Divider */}
            <div className="w-10 h-px bg-[#bbb] mb-6" />

            {/* ON AIR */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="relative flex items-center justify-center w-4 h-4">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-400/40 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                </span>
                <span className="font-sans-kr text-[11px] tracking-[0.2em] uppercase text-[#1a1a1a] font-semibold">
                  On Air
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Show 1 */}
                <div className="flex flex-col gap-2">
                  <div className="w-full">
                    <img
                      src="https://static.readdy.ai/image/7e3352e848b93c3eb9a2ea481cb871cb/daf61871d539cf8196a15cfa19735738.png"
                      alt="무엇이든 물어보살"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="font-sans-kr text-[10px] tracking-[0.15em] uppercase text-[#888]">KBSN</p>
                  <p className="font-sans-kr text-xs text-[#1a1a1a] leading-snug">&lt;무엇이든 물어보살&gt;</p>
                </div>

                {/* Show 2 */}
                <div className="flex flex-col gap-2">
                  <div className="w-full">
                    <img
                      src="https://static.readdy.ai/image/7e3352e848b93c3eb9a2ea481cb871cb/b567be8e521f5a1c21fcd9fffc6a5e93.png"
                      alt="식스센스:시티투어2"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="font-sans-kr text-[10px] tracking-[0.15em] uppercase text-[#888]">TVN</p>
                  <p className="font-sans-kr text-xs text-[#1a1a1a] leading-snug">&lt;식스센스:시티투어2&gt;</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right Column ── */}
          <div className="lg:w-[62%] flex flex-col justify-center gap-0">

            <p className="font-sans-kr text-[15px] text-[#333] font-light leading-[1.9] mb-5">
              스튜디오 디졸브는 <strong className="font-semibold text-[#1a1a1a]">발상의 전환</strong>을 만드는 콘텐츠 제작사입니다.
            </p>

            <p className="font-sans-kr text-[15px] text-[#555] font-light leading-[1.9] mb-5">
              편집에서 디졸브가 장면을 부드럽게 잇듯, 다양한 시청층과 플랫폼에 알맞은 형태로 콘텐츠를 제작하고자 합니다.
            </p>

            <p className="font-sans-kr text-[15px] text-[#555] font-light leading-[1.9] mb-5">
              스튜디오 디졸브는 <span className="text-[#1a1a1a]">"무엇을 만들까?"</span>보다<br />
              먼저 <span className="text-[#1a1a1a]">"누가, 언제, 어디서, 어떤 마음으로 보나"</span>를 묻습니다.
            </p>

            {/* Divider */}
            <div className="w-full h-px bg-[#d8d3ce] mb-5" />

            <p className="font-sans-kr text-[15px] text-[#555] font-light leading-[1.9] mb-4">
              스튜디오 디졸브의 기준은 단순합니다.
            </p>

            {/* Blockquote */}
            <blockquote className="border-l-0 pl-0 mb-5">
              <p className="font-korean-serif text-[17px] text-[#1a1a1a] italic leading-[1.85]">
                "재미는 분명하게, 메시지는 과하지 않게,<br />
                완성도는 끝까지."
              </p>
            </blockquote>

            {/* Divider */}
            <div className="w-full h-px bg-[#d8d3ce] mb-5" />

            <p className="font-sans-kr text-[15px] text-[#555] font-light leading-[1.9]">
              파트너와의 협업도 그렇습니다.{' '}
              <strong className="font-semibold text-[#1a1a1a]">같이 만들고, 같이 성장합니다.</strong>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
